import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  clearSupportComposeDraft,
  loadSupportComposeDraft,
  saveSupportComposeDraft,
} from "../../helpers/supportTicketUx";
import {
  createSupportTicket,
  SupportTicketCategory,
  SupportTicketImpact,
} from "../../services/supportTicketService";

interface FeedbackModalProps {
  isOpen: boolean;
  toggle: () => void;
  mode?: "ticket" | "feedback";
  onCreated?: () => void;
}

const CATEGORIES: SupportTicketCategory[] = [
  "agenda",
  "orders",
  "payments",
  "account",
  "integration",
  "other",
];

const IMPACTS: SupportTicketImpact[] = ["blocks", "annoyance", "nice_to_have"];

const SupportTicketModal = ({
  isOpen,
  toggle,
  mode = "ticket",
  onCreated,
}: FeedbackModalProps) => {
  const { t } = useTranslation();
  const isFeedback = mode === "feedback";

  const [category, setCategory] = useState<SupportTicketCategory>(
    isFeedback ? "product_feedback" : "agenda"
  );
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [impact, setImpact] = useState<SupportTicketImpact | "">("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draftHint, setDraftHint] = useState(false);

  const resetForm = () => {
    setCategory(isFeedback ? "product_feedback" : "agenda");
    setSubject("");
    setDescription("");
    setImpact("");
    setFiles([]);
    setErrors({});
    setDraftHint(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const draft = loadSupportComposeDraft(mode);

    if (draft) {
      if (!isFeedback && draft.category) {
        setCategory(draft.category as SupportTicketCategory);
      } else {
        setCategory(isFeedback ? "product_feedback" : "agenda");
      }

      setSubject(draft.subject);
      setDescription(draft.description);
      setImpact(
        isFeedback && draft.impact
          ? (draft.impact as SupportTicketImpact)
          : ""
      );
      setDraftHint(true);
    } else {
      resetForm();
    }

    setFiles([]);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore draft only when modal opens / mode changes
  }, [isOpen, mode, isFeedback]);

  useEffect(() => {
    if (!isOpen || submitting) {
      return;
    }

    const timer = window.setTimeout(() => {
      saveSupportComposeDraft(mode, {
        category: isFeedback ? undefined : category,
        subject,
        description,
        impact: isFeedback ? impact || undefined : undefined,
      });
      setDraftHint(
        subject.trim() !== "" || description.trim() !== "" || Boolean(impact)
      );
    }, 400);

    return () => window.clearTimeout(timer);
  }, [
    category,
    description,
    impact,
    isFeedback,
    isOpen,
    mode,
    subject,
    submitting,
  ]);

  const handleClose = () => {
    if (submitting) {
      return;
    }

    saveSupportComposeDraft(mode, {
      category: isFeedback ? undefined : category,
      subject,
      description,
      impact: isFeedback ? impact || undefined : undefined,
    });
    toggle();
  };

  const handleClearDraft = () => {
    clearSupportComposeDraft(mode);
    resetForm();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};

    if (!subject.trim()) {
      nextErrors.subject = t("support.validation.subjectRequired");
    }

    if (!description.trim()) {
      nextErrors.description = t("support.validation.descriptionRequired");
    }

    if (isFeedback && !impact) {
      nextErrors.impact = t("support.validation.impactRequired");
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await createSupportTicket({
        category: isFeedback ? "product_feedback" : category,
        subject: subject.trim(),
        description: description.trim(),
        impact: isFeedback ? (impact as SupportTicketImpact) : undefined,
        page_url: window.location.pathname,
        attachments: files,
      });

      if (response.success) {
        toast.success(
          t("support.toast.created", { number: response.data.public_number })
        );
        clearSupportComposeDraft(mode);
        resetForm();
        toggle();
        onCreated?.();
      }
    } catch (error: unknown) {
      const apiError = error as {
        message?: string;
        details?: Record<string, string[]>;
      };
      const details = apiError.details || {};
      const mapped: Record<string, string> = {};

      Object.entries(details).forEach(([key, messages]) => {
        mapped[key] = messages[0];
      });

      setErrors(mapped);
      toast.error(apiError.message || t("support.toast.createError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {isFeedback ? t("support.feedback.title") : t("support.ticket.title")}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <p className="text-muted small">
            {isFeedback
              ? t("support.feedback.intro")
              : t("support.ticket.intro")}
          </p>

          {draftHint ? (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                <i className="ri-save-line me-1" />
                {t("support.compose.draftSaved")}
              </small>
              <Button
                color="link"
                size="sm"
                className="p-0"
                type="button"
                onClick={handleClearDraft}
              >
                {t("support.compose.clearDraft")}
              </Button>
            </div>
          ) : null}

          {!isFeedback ? (
            <FormGroup>
              <Label for="support-category">{t("support.fields.category")}</Label>
              <Input
                id="support-category"
                type="select"
                value={category}
                onChange={(event) =>
                  setCategory(event.target.value as SupportTicketCategory)
                }
              >
                {CATEGORIES.map((item) => (
                  <option key={item} value={item}>
                    {t(`support.categories.${item}`)}
                  </option>
                ))}
              </Input>
            </FormGroup>
          ) : null}

          <FormGroup>
            <Label for="support-subject">{t("support.fields.subject")}</Label>
            <Input
              id="support-subject"
              value={subject}
              invalid={Boolean(errors.subject)}
              onChange={(event) => setSubject(event.target.value)}
              maxLength={255}
            />
            {errors.subject ? <FormFeedback>{errors.subject}</FormFeedback> : null}
          </FormGroup>

          <FormGroup>
            <Label for="support-description">
              {isFeedback
                ? t("support.fields.whatHappened")
                : t("support.fields.description")}
            </Label>
            <Input
              id="support-description"
              type="textarea"
              rows={4}
              value={description}
              invalid={Boolean(errors.description)}
              onChange={(event) => setDescription(event.target.value)}
            />
            {errors.description ? (
              <FormFeedback>{errors.description}</FormFeedback>
            ) : null}
          </FormGroup>

          {isFeedback ? (
            <FormGroup>
              <Label for="support-impact">{t("support.fields.impact")}</Label>
              <Input
                id="support-impact"
                type="select"
                value={impact}
                invalid={Boolean(errors.impact)}
                onChange={(event) =>
                  setImpact(event.target.value as SupportTicketImpact | "")
                }
              >
                <option value="">{t("support.fields.impactPlaceholder")}</option>
                {IMPACTS.map((item) => (
                  <option key={item} value={item}>
                    {t(`support.impacts.${item}`)}
                  </option>
                ))}
              </Input>
              {errors.impact ? <FormFeedback>{errors.impact}</FormFeedback> : null}
            </FormGroup>
          ) : null}

          <FormGroup>
            <Label for="support-attachments">{t("support.fields.attachment")}</Label>
            <Input
              id="support-attachments"
              type="file"
              accept=".png,.jpg,.jpeg,.pdf"
              multiple
              onChange={(event) => {
                const selected = Array.from(event.target.files || []).slice(0, 3);
                setFiles(selected);
              }}
            />
            <small className="text-muted">{t("support.fields.attachmentHint")}</small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" onClick={handleClose} disabled={submitting}>
            {t("support.actions.cancel")}
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : t("support.actions.send")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default SupportTicketModal;
